import Typography from "@mui/material/Typography";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Box from "@mui/material/Box";
import { styled } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import Guid from "_shared/guids/guid";
import Stack from "@mui/material/Stack";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualUnavailabilityByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { Masked } from "work/masked-fields";
import type { RemoveFromWork } from "work/work-agreements/teams/_views/remove-from-work-row-actions";
import VendorTeamReadOnly from "work/work-agreements/teams/vendor-team/_views/vendor-team-readonly-view";
import VendorTeamRestaffing from "work/work-agreements/teams/vendor-team/_views/vendor-team-restaffing-view";
import WorkAgreement from "work/work-agreements/work-agreement";
import { workPaneNarrowerThan, workPaneProseInset } from "work/_views/work-pane-layout";

/**
 * No indent of its own: the roster runs the width of the tab, as the proposal's does, and the line
 * above it takes the prose inset so it stands on the actions row's left edge. The column and the
 * roster each used to add a margin, which put the table 40px right of the Leave Team button.
 */
const ListContainer = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
  height: "100%",
  marginTop: theme.spacing(2),
  minHeight: "20rem",
}));
// The restriction is stated by what it means for the roster, not as a bare flag with the meaning
// hidden in a tooltip: the reader should not have to hover to learn whether the team can change.
const RestrictionLine = styled(Box)(({ theme }) => ({
  ...workPaneProseInset(theme),
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
}));
const RestrictionLabel = styled("strong")(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
}));
const TeamReview = styled(VendorTeamReadOnly)(({ theme }) => ({
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    marginTop: theme.spacing(2),
  },
}));
const TeamRestaffing = styled(VendorTeamRestaffing)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
}));

type TeamTabProps = {
  entityId: Guid;
  workAgreement: WorkAgreement;
  refreshWorkAgreement: () => unknown;
  /** The client team leader's removal of a member from running work (#6070), when the reader is them. */
  removeFromWork?: RemoveFromWork;
};

export default function TeamTab(props: Readonly<TeamTabProps>) {
  const { workAgreement, refreshWorkAgreement } = props;
  const session = useSession();

  // Everyone still on the team. Kept apart from the departed ids below so that the two lists can be
  // told apart again once the directory has answered for all of them in one query.
  const rosterUserIds: Set<string> = useMemo(() => {
    const vendorTeam = workAgreement.vendorTeam;
    if (Masked.isMasked(vendorTeam) || !vendorTeam?.leader) return new Set<string>();

    return new Set([
      vendorTeam.leader.userId.value,
      ...vendorTeam.members.map((member) => member.userId.value),
    ]);
  }, [workAgreement.vendorTeam]);

  // `vendorTeamDepartures` hands back a copy on every read, so the list is pinned to the agreement it
  // came from. Without this the ids below would be rebuilt every render and the directory query would
  // be asked again with an argument that only looks new. A deliberate stability guarantee, not
  // defensive memoization.
  const departures = useMemo(() => workAgreement.vendorTeamDepartures, [workAgreement]);
  const departedUserIds: Set<string> = useMemo(
    () => new Set(departures.map((departure) => departure.userId.value)),
    [departures],
  );

  const individualUserIds: Set<string> = useMemo(
    () => new Set([...rosterUserIds, ...departedUserIds]),
    [departedUserIds, rosterUserIds],
  );

  const individualQuery = useGetIndividualsByUserIdsQuery(
    individualUserIds.size ? { userIds: [...individualUserIds] } : skipToken,
  );

  const unavailability = useSelector(getIndividualUnavailabilityByUserIds);
  const individuals = useMemo(() => {
    if (!individualQuery.data) return [];
    return Individual.fromJSONArray(individualQuery.data) ?? [];
  }, [individualQuery.data]);

  const leader = individuals.find(
    (ind: Individual) =>
      Masked.isPresentNotMasked(workAgreement.vendorTeam) && ind.userId.isEqualTo(workAgreement.vendorTeam.leader?.userId),
  );

  const members = individuals.filter(
    (ind: Individual) =>
      Masked.isNotMasked(workAgreement.vendorTeam) &&
      rosterUserIds.has(ind.userId.value) &&
      !ind.userId.isEqualTo(workAgreement.vendorTeam?.leader?.userId),
  );

  // Somebody who left and was never put back is on the agreement in no role, so they must not reach
  // the roster's member rows — `vendorTeamDepartureRows` draws them, with the reason they left.
  const departedIndividuals = individuals.filter((ind: Individual) => departedUserIds.has(ind.userId.value));

  if (Masked.isMasked(workAgreement.vendorTeam) || Masked.isMasked(workAgreement.teamRestricted)) {
    return "You do not have permission to view this team.";
  }

  const vendorTeam = workAgreement.vendorTeam;
  /**
   * What the reader may do with this roster, or nothing when all they may do is read it.
   *
   * The roster is the VENDOR's own staffing, so only the person leading that team for the current
   * Legal Entity may change it — the client's route to a different team is Revise, and
   * `PUT work/{id}/vendor-team-members` answers anybody else with a 403. It is staffing rather
   * than a term only while
   * `vendorTeamRosterIsAnAgreedTerm` is false, which is the predicate the endpoint itself turns on.
   * And it is only worth changing on work that is actually running: the endpoint would take a
   * restricted roster on ended work in place, but nobody is staffed on ended work, so the tab reads
   * as the record it is.
   */
  const restaffing =
    vendorTeam &&
    workAgreement.id &&
    workAgreement.version !== undefined &&
    vendorTeam.leader?.userId.isEqualTo(session.user?.id) &&
    vendorTeam.leader.legalEntityId.isEqualTo(session.user?.legalEntityId) &&
    workAgreement.isActiveWork &&
    !workAgreement.vendorTeamRosterIsAnAgreedTerm
      ? { team: vendorTeam, workAgreementId: workAgreement.id }
      : undefined;

  return (
    <ListContainer>
      <RestrictionLine>
        {workAgreement.teamRestricted.value ? (
          <LockOutlinedIcon fontSize="small" color="warning" />
        ) : (
          <LockOpenOutlinedIcon fontSize="small" color="action" />
        )}
        <Typography variant="body2" color="text.secondary">
          {workAgreement.teamRestricted.value ? (
            <>
              <RestrictionLabel>Restricted team</RestrictionLabel> · Changing members needs a revised
              agreement.
            </>
          ) : (
            <>
              <RestrictionLabel>Unrestricted team</RestrictionLabel> · Members can change without
              renegotiating.
            </>
          )}
        </Typography>
      </RestrictionLine>
      {restaffing ? (
        <TeamRestaffing
          workAgreementId={restaffing.workAgreementId}
          version={workAgreement.version!}
          team={restaffing.team}
          individuals={individuals}
          unavailability={unavailability}
          refreshWorkAgreement={refreshWorkAgreement}
          departures={departures}
          workAgreement={workAgreement}
        />
      ) : (
        <TeamReview
          leader={leader ?? null}
          members={members}
          departures={departures}
          departedIndividuals={departedIndividuals}
          removeFromWork={props.removeFromWork}
        />
      )}
    </ListContainer>
  );
}
